import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider } from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider, LinkObjectType } from "../providers/types";

const PLURAL = { contact: "contacts", company: "companies", deal: "deals" };

export interface PickedRecord {
  id: string;
  label: string;
}

/** A match's name; a contact also shows its primary type, to tell namesakes apart. */
const labelOf = (row: Record<string, unknown>) => {
  const name = String(
    row.name ?? [row.first_name, row.last_name].filter(Boolean).join(" "),
  );
  return row.primary_type
    ? `${name} · ${String(row.primary_type).replace(/_/g, " ")}`
    : name;
};

/**
 * Find a contact, company or deal by name and pick it (W9 F5, W10). Shows the
 * pick once made; typing again clears it. Give it a `key` that changes with
 * `objectType` so a new kind of record starts a fresh search.
 */
export function RecordPicker({
  id,
  label,
  objectType,
  picked,
  onPick,
  excludeId,
  pickedText = "Picked",
}: {
  id: string;
  label: string;
  objectType: LinkObjectType;
  picked: PickedRecord | null;
  onPick: (record: PickedRecord | null) => void;
  /** Leave this record out of the matches (the one being edited). */
  excludeId?: string;
  pickedText?: string;
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [search, setSearch] = useState("");
  const term = search.trim();
  const matches = useQuery({
    queryKey: ["record_picker", objectType, term],
    enabled: term.length >= 2,
    queryFn: async () => {
      const { data } = await dataProvider.getList(PLURAL[objectType], {
        filter: { q: term },
        pagination: { page: 1, perPage: 8 },
        sort: { field: "id", order: "ASC" },
      });
      return data
        .filter((row) => row.id !== excludeId)
        .map((row) => ({ id: String(row.id), label: labelOf(row) }));
    },
  });

  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={search}
        placeholder="Type at least two letters"
        onChange={(event) => {
          setSearch(event.target.value);
          onPick(null);
        }}
      />
      {picked ? (
        <p className="text-sm">
          {pickedText} <strong>{picked.label}</strong>
        </p>
      ) : (
        <ul aria-label="Matches" className="space-y-1">
          {(matches.data ?? []).map((match) => (
            <li key={match.id}>
              <Button
                type="button"
                size="sm"
                variant="link"
                className="h-auto p-0"
                onClick={() => onPick(match)}
              >
                {match.label}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
